import { useState, useRef, useEffect, useCallback, ReactNode } from "react";

type L3LazyLoadedListProps<T> = {
	items: T[]
	itemIcrement: number
	builder: (model: T, idx: number) => ReactNode
}

function L3LazyLoadedList<T>({ items, itemIcrement = 10, builder }: L3LazyLoadedListProps<T>) {
	const [renderedItems, setRenderedItems] = useState<T[]>([]);
	const [page, setPage] = useState(0);
	const observer = useRef<IntersectionObserver|null>(null);
	const lastItemRef = useRef(null);
	const hasMore = page * itemIcrement < items.length

	useEffect(() => {
		setPage(0)
		setRenderedItems([])
	}, [items]);

	useEffect(() => {
		if(page === 0) {
			loadMoreItems()
		}
	}, [page])

	const loadMoreItems = useCallback(() => {
		if(!hasMore) return

		const newItems = items.slice(page * itemIcrement, (page + 1) * itemIcrement)

		setRenderedItems((prevItems) => [...prevItems, ...newItems]);
		setPage((prevPage) => prevPage + 1);
	}, [items, itemIcrement, page]);

	useEffect(() => {
		if (observer.current) observer.current.disconnect();
		
		observer.current = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) {
				loadMoreItems();
			}
		});

		const currentRef = lastItemRef.current;
		if (currentRef) observer.current.observe(currentRef);

		return () => {
			if (observer.current && currentRef) observer.current.unobserve(currentRef);
		};
	}, [loadMoreItems]);

  	return (
    	<>
			{
				renderedItems.map((item, index) => (
        			<div key={index} ref={index === renderedItems.length - 1 ? lastItemRef : null}>
						{
							builder(item, index)
						}
        			</div>
				))
			}
    	</>
  	);
};

export default L3LazyLoadedList;
